import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { copy } from "shared/assets";
import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { IAPIState, IStatus } from "shared/types";

import {
  StageCapacity,
  DeleteCapacityModal,
  EditCapacityModal,
} from "Venues/components";
import { useStagePageContext } from "Venues/contexts";
import {
  clearActionMeta,
  createStageCapacity,
  dataMetaSelector,
  deleteStageCapacity,
  editStageCapacity,
} from "Venues/state";
import { IStageCapacity, VENUE_ACTIONS } from "Venues/types";

const { editStage } = copy;

export const EditStageCapacity: React.FC = () => {
  const [resetAddForm, setResetAddForm] = useState(false);
  const [selectedCapacity, setSelectedCapacity] = useState<IStageCapacity>();
  const [showDeleteCapacityModal, setShowDeleteCapacityModal] = useState(false);
  const [showEditCapacityModal, setShowEditCapacityModal] = useState(false);

  const { stage, stageId, fetchStage } = useStagePageContext();
  const [capacities, setCapacities] = useState<IStageCapacity[]>(
    stage?.stageCapacities || []
  );

  const dispatch = useDispatch();
  const { api: addCapacityMeta, id: addedCapacityId } = useSelector(
    dataMetaSelector(VENUE_ACTIONS.createStageCapacity)
  );
  const { api: deleteCapacityMeta } = useSelector(
    dataMetaSelector(VENUE_ACTIONS.deleteStageCapacity)
  );
  const { api: editCapacityMeta } = useSelector(
    dataMetaSelector(VENUE_ACTIONS.editStageCapacity)
  );

  const stagePath = useMemo(
    () => (stageId ? API_ENDPOINTS.STAGE(stageId) : ""),
    [stageId]
  );

  // delete capacity success
  useEffect(() => {
    if (deleteCapacityMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(VENUE_ACTIONS.deleteStageCapacity));
      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: editStage.capacities.delete,
        })
      );
      setCapacities((capacities) =>
        capacities.filter((capacity) => capacity.id !== selectedCapacity?.id)
      );
      setShowDeleteCapacityModal(false);
    }
  }, [deleteCapacityMeta, selectedCapacity, dispatch]);

  // edit capacity success
  useEffect(() => {
    if (editCapacityMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(VENUE_ACTIONS.editStageCapacity));
      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: editStage.capacities.edit,
        })
      );
      selectedCapacity &&
        setCapacities((capacities) =>
          capacities.map((oldCapacity) =>
            oldCapacity.id === selectedCapacity.id
              ? selectedCapacity
              : oldCapacity
          )
        );
      setShowEditCapacityModal(false);
    }
  }, [editCapacityMeta, selectedCapacity, dispatch]);

  // add capacity success
  useEffect(() => {
    if (addCapacityMeta?.status === IAPIState.succeeded) {
      fetchStage();
      setResetAddForm(true);
      dispatch(clearActionMeta(VENUE_ACTIONS.createStageCapacity));
      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: editStage.capacities.add,
        })
      );
      selectedCapacity &&
        addedCapacityId &&
        setCapacities((capacities) => [
          ...capacities,
          { ...selectedCapacity, id: addedCapacityId },
        ]);
    }
  }, [
    addCapacityMeta,
    selectedCapacity,
    dispatch,
    addedCapacityId,
    fetchStage,
  ]);

  useEffect(() => {
    if (resetAddForm) {
      setResetAddForm(false);
    }
  }, [resetAddForm]);

  const handleAdd = (capacity: IStageCapacity) => {
    dispatch(createStageCapacity({ stagePath, capacity }));
    setSelectedCapacity(capacity);
  };

  const handleDelete = (id?: string) => {
    id && stageId && dispatch(deleteStageCapacity({ id, stageId }));
  };

  const handleDeleteModalClose = () => {
    setShowDeleteCapacityModal(false);
    dispatch(clearActionMeta(VENUE_ACTIONS.deleteStageCapacity));
  };

  const handleEdit = (capacityData: IStageCapacity, capacityId?: string) => {
    capacityId &&
      stageId &&
      dispatch(editStageCapacity({ stageId, capacityId, capacityData }));
    capacityId && setSelectedCapacity({ ...capacityData, id: capacityId });
  };

  const handleEditModalClose = () => {
    setShowEditCapacityModal(false);
    dispatch(clearActionMeta(VENUE_ACTIONS.editStageCapacity));
  };

  return (
    <>
      {showDeleteCapacityModal && (
        <DeleteCapacityModal
          handleClose={handleDeleteModalClose}
          capacity={selectedCapacity}
          handleDelete={handleDelete}
          apiMeta={deleteCapacityMeta}
        />
      )}
      {showEditCapacityModal && (
        <EditCapacityModal
          handleClose={handleEditModalClose}
          capacity={selectedCapacity}
          handleEdit={handleEdit}
          apiMeta={editCapacityMeta}
        />
      )}

      <StageCapacity
        capacities={capacities}
        isSubmitting={addCapacityMeta?.status === IAPIState.pending}
        addCapacity={handleAdd}
        handleDelete={(capacity: IStageCapacity) => {
          setSelectedCapacity(capacity);
          setShowDeleteCapacityModal(true);
        }}
        handleEdit={(capacity: IStageCapacity) => {
          setSelectedCapacity(capacity);
          setShowEditCapacityModal(true);
        }}
        hasError={!!addCapacityMeta?.error}
        resetForm={resetAddForm}
      />
    </>
  );
};
